import React, { useEffect, useState, useMemo } from "react";
import { useUserContext } from "../hooks/UserContext";
import { Box, CircularProgress, Typography, Alert, TextField, Button, Checkbox, FormControl, InputLabel, ListItemText, MenuItem, Select, FormControlLabel } from "@mui/material";
import DynamicGrid from "../components/DynamicTable";
import LocalSelector from "./components/LocalSelector";
import { useNavigate } from "react-router-dom";
import EditIcon from "@mui/icons-material/Edit";
import { useAppConfig } from "../hooks/AppConfigContext";
import { useProductosVenta } from "../hooks/ProductosVentaContext";
import { fetchDescargarCatalogoPdf } from "../services/productoService";

export const listaPreciosGridColumnDefinitions = {
  'CODIGO': { Header: 'Código', Editable: false },
  'PRODUCTO': { Header: 'Producto', Editable: false },
  'CONTADO': { Header: 'Contado $', Editable: false, IsNumber: true, Currency: true },
  'MAYOR': { Header: 'Mayor $', Editable: false, IsNumber: true, Currency: true },
  'MENOR': { Header: 'Menor $', Editable: false, IsNumber: true, Currency: true },
  'EDITAR': { Header: 'Acciones', Editable: false },
};

const ListaPrecios = () => {
  const { getUser, isLoggedIn } = useUserContext();
  const { productoVentaHeaders } = useAppConfig();
  const navigate = useNavigate();
  const [idLocal, setIdLocal] = useState<string | undefined>(undefined);
  const { productos, loading, error } = useProductosVenta(idLocal);
  const [filterText, setFilterText] = useState<string>("");
  const [incluirFotos, setIncluirFotos] = useState(false);
  const [descargandoCatalogo, setDescargandoCatalogo] = useState(false);
  const [columnas, setColumnas] = useState<string[]>(["CODIGO", "PRODUCTO", "CONTADO"]);

  const handleLocalChange = (idLocal: string | undefined) => {
    setIdLocal(idLocal);
  };

  const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilterText(event.target.value);
  };

  // Filter products by code or description.
  const filteredProductos = useMemo(() => {
    if (!filterText) return productos;
    const normalizedFilter = filterText.toLowerCase();
    return productos.filter((producto) =>
      producto.CODIGO.toLowerCase().includes(normalizedFilter) ||
      producto.PRODUCTO.toLowerCase().includes(normalizedFilter)
    );
  }, [productos, filterText]);

  useEffect(() => {
    if (!filterText.trim() || filteredProductos.length !== 1) return;

    const clearSearchTimer = window.setTimeout(() => {
      setFilterText("");
    }, 30000);

    return () => window.clearTimeout(clearSearchTimer);
  }, [filterText, filteredProductos.length]);

  // Check if user is authorized
  const user = getUser();
  const hasPermission = user?.permissions?.includes("lista_precios");
  const canUpdatePrices = user?.permissions?.includes("actualizar_precios");
  const listaPreciosHeaders: Record<string, { Header: string; Editable?: boolean; IsNumber?: boolean; Currency?: boolean }> = {
    ...productoVentaHeaders,
    EDITAR: { Header: "Acciones", Editable: false }
  };
  const columnasCatalogoPermitidas = ["CODIGO", "PRODUCTO", "DESCRIPCION", "PRESENTACION", "RUBRO", "SUBRUBRO", "CONTADO", "MAYOR", "MENOR"];
  const columnasDisponibles = Object.keys(listaPreciosHeaders).filter((key) => columnasCatalogoPermitidas.includes(key));

  useEffect(() => {
    const iniciales = ["CODIGO", "PRODUCTO", "CONTADO", "MAYOR", "MENOR"].filter((key) => columnasDisponibles.includes(key));
    setColumnas((actuales) => actuales.filter((key) => columnasDisponibles.includes(key)).length ? actuales.filter((key) => columnasDisponibles.includes(key)) : iniciales);
  }, [productoVentaHeaders]);

  const headersSeleccionados = Object.fromEntries(
    Object.entries(listaPreciosHeaders).filter(([key]) => columnas.includes(key))
  );

  const descargarCatalogo = async () => {
    const user = getUser();
    if (!user || !idLocal || columnas.length === 0 || filteredProductos.length === 0) return;
    setDescargandoCatalogo(true);
    try {
      const filas = filteredProductos.map((producto: any) => ({
        idProducto: Number(producto.IDPRODUCTO ?? producto.idProducto),
        codigo: String(producto.CODIGO ?? ""),
      }));
      const blob = await fetchDescargarCatalogoPdf(user, Number(idLocal), incluirFotos, columnas, filas);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `Catalogo_Productos_${idLocal}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      alert(error?.message ?? "No se pudo generar el catálogo PDF");
    } finally {
      setDescargandoCatalogo(false);
    }
  };

  // Show unauthorized message if user doesn't have permission
  if (!isLoggedIn()) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">Por favor, inicie sesión para acceder a esta página.</Alert>
      </Box>
    );
  }

  if (!hasPermission) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">No tiene permisos para acceder a la lista de precios.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Lista de Precios
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        La lista se mantiene sincronizada automáticamente. Si cambian precios o productos habilitados para vender, se actualizará sin recargar la página.
      </Typography>

      <Box sx={{ mb: 3 }}>
        <LocalSelector onLocalChange={handleLocalChange} />
      </Box>

      {productos.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <TextField
            label="Buscar por código o producto"
            variant="outlined"
            fullWidth
            value={filterText}
            onChange={handleFilterChange}
            placeholder="Ingrese el código o nombre del producto..."
            helperText="Puede buscar por una parte del código o del nombre. Si queda un solo producto, la búsqueda se limpia después de 30 segundos."
          />
        </Box>
      )}

      <Box sx={{ mb: 2, display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
        <FormControl size="small" sx={{ minWidth: 260 }}>
          <InputLabel id="catalogo-columnas-label">Columnas del catálogo</InputLabel>
          <Select
            labelId="catalogo-columnas-label"
            multiple
            value={columnas}
            label="Columnas del catálogo"
            onChange={(event) => setColumnas(typeof event.target.value === "string" ? event.target.value.split(",") : event.target.value)}
            renderValue={(selected) => selected.join(", ")}
          >
            {columnasDisponibles.map((key) => (
              <MenuItem key={key} value={key}><Checkbox checked={columnas.includes(key)} /><ListItemText primary={listaPreciosHeaders[key]?.Header ?? key} /></MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControlLabel control={<Checkbox checked={incluirFotos} onChange={(event) => setIncluirFotos(event.target.checked)} />} label="Incluir fotos" />
        <Button variant="outlined" onClick={descargarCatalogo} disabled={!idLocal || filteredProductos.length === 0 || columnas.length === 0 || descargandoCatalogo}>
          {descargandoCatalogo ? "Generando PDF..." : "Descargar catálogo PDF"}
        </Button>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        El catálogo utiliza todas las filas que coinciden con la búsqueda y respeta su orden. Seleccione las columnas permitidas y, opcionalmente, incluya la foto principal de cada producto.
      </Typography>

      {loading ? (
        <Box sx={{ textAlign: "center", mt: 3 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error">Error al cargar la lista de productos</Alert>
      ) : filteredProductos.length > 0 ? (
        <DynamicGrid
          data={filteredProductos}
          customHeaderMap={headersSeleccionados}
          pageSize={10}
          removeIfNotCustom={true}
          customRenderers={
            canUpdatePrices
              ? {
                  EDITAR: (row, rowIndex) => (
                    <Button
                      onClick={() => navigate(`/updateproducto/${row.IDPRODUCTO}`)}
                      variant="contained"
                      color="primary"
                      size="small"
                      startIcon={<EditIcon />}
                    >
                      Editar
                    </Button>
                  ),
                }
              : undefined
          }
        />
      ) : productos.length > 0 ? (
        <Typography variant="body1" color="textSecondary">
          No se encontraron productos que coincidan con "{filterText}"
        </Typography>
      ) : (
        <Typography variant="body1" color="textSecondary">
          Seleccione un local para ver la lista de productos
        </Typography>
      )}
    </Box>
  );
};

export default ListaPrecios;
